import {
  EntityType,
  FilterEntityEnum,
  OrganizationService,
} from "@propertelligent/client-api";
import { useCallback, useMemo, useState } from "react";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import BluePayableIcon from "../../assets/Payment-Icon.svg";
import AddTraspButton from "../../components/firstTimeUserExperience/commonCom/AddTraspButton";
import CustomTooltip from "../../components/common/CustomTooltip";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import SeparaterLine from "../../components/common/SeparaterLine";
import { UserLoginData } from "../../components/common/charts/Enums";
import SimpleSpinner from "../../components/loader";
import ActionMenu from "../actionMenus/action-menu";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import EmptyState from "../emptystate";
import EditTransactionDrawer from "./EditTransactionDrawer";
import apiHelper from "../../utils/apiHelper";
import { GetImages } from "../../utils/GetImages";
import { useGeneratePdfContext } from "../../utils/context/GeneratePdfContext";
import { usePermissions } from "../../hooks/usePermissions";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import { refundOrganizationTransaction } from "../../services/organizationTransaction.service";
import {
  canDeleteTransaction,
  canRefundTransaction,
} from "./transactionActionUtils";
import type { TransactionRow } from "./transactionTypes";

interface TransactionTableProps {
  organizationDataTX: any[];
  organizationData: TransactionRow[];
  propertyData: TransactionRow[];
  propertyDataTX: TransactionRow[];
  unitData: TransactionRow[];
  organizationsResponse: (id: number) => void;
  propertyResponse: (id: number | null) => void;
  unitResponse: (id: number | null) => void;
  setSelectedPropertyId: (id: number | null) => void;
  setSelectedUnitId: (id: number | null) => void;
  propertyResponseTX: (id: number | null) => void;
  selectedPropertyId: number | null;
  selectedUnitId: number | null;
  orgLoading: boolean;
  unitLoading: boolean;
  propertyLoading: boolean;
  loading: boolean;
  selectedCategories: Record<string, string[]>;
  setSelectedCategories: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  organizationsTXResponse: () => void;
  payeeAccountData: any[];
  paymentMethodsArr: any[];
  canManageTransactions?: boolean;
  onApplyFilter: (
    currentOption: FilterEntityEnum,
    currentIds: number | number[],
    currentCategories: Record<string, string[]>
  ) => void;
  resetFilters: () => void;
}

const TransactionTable = ({
  organizationDataTX,
  organizationData,
  propertyData,
  propertyDataTX,
  unitData,
  organizationsResponse,
  propertyResponse,
  unitResponse,
  setSelectedPropertyId,
  setSelectedUnitId,
  propertyResponseTX,
  selectedPropertyId,
  selectedUnitId,
  orgLoading,
  unitLoading,
  propertyLoading,
  loading,
  selectedCategories,
  setSelectedCategories,
  organizationsTXResponse,
  payeeAccountData,
  paymentMethodsArr,
  canManageTransactions = false,
  onApplyFilter,
  resetFilters,
}: TransactionTableProps) => {
  const [expandedOrganisationIndex, setExpandedOrganisationIndex] =
    useState<number | null>(null);
  const [expandedPropertyIndex, setExpandedPropertyIndex] =
    useState<number | null>(null);
  const [expandedUnitIndex, setExpandedUnitIndex] = useState<number | null>(
    null,
  );
  const [transactionToEdit, setTransactionToEdit] =
    useState<TransactionRow | null>(null);
  const [transactionToDelete, setTransactionToDelete] =
    useState<TransactionRow | null>(null);
  const [transactionToRefund, setTransactionToRefund] =
    useState<TransactionRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id));
  const {
    selectedValues,
    setSelectedValues,
    selectedIds,
    setSelectedIds,
    selectedOption,
  } = useGeneratePdfContext();
  const { canWrite } = usePermissions();
  const hasWritePermission = canWrite("Transaction");
  const showActions = canManageTransactions && hasWritePermission;
  const actionColumnSpan = showActions ? 10 : 9;

  const categories = selectedCategories.TranactionCreditDebitTypeId;
  const payeeAccount = selectedCategories["payeeAccount.accountName"];
  const paymentMethod = selectedCategories["paymentMethod.PaymentMethodTypeId"];
  const payer = selectedCategories["paymentMethod.accountName"];

  const publishFailure = useCallback((message: string) => {
    PubSub.publish(TopicsEnum.Notification.toString(), {
      type: NotificationType.Failure,
      message,
    });
  }, []);

  const refreshVisibleData = useCallback(() => {
    organizationsTXResponse();

    const expandedGroup =
      expandedOrganisationIndex != null
        ? organizationDataTX[expandedOrganisationIndex]
        : expandedPropertyIndex != null
          ? organizationDataTX[expandedPropertyIndex]
          : null;

    if (expandedGroup?.entityId) {
      organizationsResponse(expandedGroup.entityId);
    }

    if (selectedPropertyId) {
      propertyResponseTX(selectedPropertyId);
      propertyResponse(selectedPropertyId);
    }

    if (selectedUnitId) {
      unitResponse(selectedUnitId);
    }
  }, [
    expandedOrganisationIndex,
    expandedPropertyIndex,
    organizationDataTX,
    organizationsResponse,
    organizationsTXResponse,
    propertyResponse,
    propertyResponseTX,
    selectedPropertyId,
    selectedUnitId,
    unitResponse,
  ]);

  const handleDelete = useCallback(async () => {
    if (!transactionToDelete?.id || isDeleting) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await apiHelper(
        OrganizationService.deleteApiOrganizationTransaction,
        {
          successMessage: "Successfully Deleted Transaction",
          showNotification: true,
        },
        transactionToDelete.id,
      );

      if (response?.data?.success) {
        setTransactionToDelete(null);
        refreshVisibleData();
        return;
      }

      if (!response?.statusCode && !response?.errors) {
        publishFailure("Failed to delete transaction. Try again.");
      }
    } catch (error) {
      publishFailure("Failed to delete transaction. Try again.");
    } finally {
      setIsDeleting(false);
    }
  }, [
    isDeleting,
    publishFailure,
    refreshVisibleData,
    transactionToDelete,
  ]);

  const handleRefund = useCallback(async () => {
    if (!transactionToRefund?.id || isRefunding) {
      return;
    }

    setIsRefunding(true);
    try {
      const response = await apiHelper(
        refundOrganizationTransaction,
        {
          successMessage: "Successfully Refunded Transaction",
          showNotification: true,
        },
        transactionToRefund.id,
      );

      if (response?.data?.success) {
        setTransactionToRefund(null);
        refreshVisibleData();
        return;
      }

      if (!response?.statusCode && !response?.errors) {
        publishFailure("Failed to refund transaction. Try again.");
      }
    } catch (error) {
      publishFailure("Failed to refund transaction. Try again.");
    } finally {
      setIsRefunding(false);
    }
  }, [
    isRefunding,
    publishFailure,
    refreshVisibleData,
    transactionToRefund,
  ]);

  const buildMenuItems = useCallback((transaction: TransactionRow) => {
    const items = [
      {
        icon: editIcon,
        label: "Edit",
        action: () => setTransactionToEdit(transaction),
        isDestructive: false,
      },
    ];

    if (canDeleteTransaction(transaction)) {
      items.push({ isDivider: true } as any);
      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: () => setTransactionToDelete(transaction),
        isDestructive: true,
      } as any);
    }

    if (canRefundTransaction(transaction)) {
      items.push({ isDivider: true } as any);
      items.push({
        icon: BluePayableIcon,
        label: "Refund",
        action: () => setTransactionToRefund(transaction),
        isDestructive: false,
      } as any);
    }

    return items;
  }, []);

  const renderActions = useCallback(
    (transaction: TransactionRow) => {
      if (!showActions) {
        return null;
      }

      return (
        <td className="px-2 normal_text">
          <ActionMenu
            Id={transaction.id}
            menuItems={buildMenuItems(transaction)}
            trigger={
              <button
                type="button"
                className="action-button iconStyleWithWhiteFilter"
                aria-label="More options"
              >
                <img src={dropdownIcon} alt="More options" />
              </button>
            }
          />
        </td>
      );
    },
    [buildMenuItems, showActions],
  );

  const renderTransactionRow = useCallback(
    (item: TransactionRow, key: string | number) => (
      <tr key={key} className="tooltip-main-row" style={{ height: "unset" }}>
        <td style={{ borderLeft: "none", borderBottom: "none", borderTop: "none" }}>
          <div className="normal_text ps-3 h-100">
            <span
              style={{ borderLeft: "1px solid var(--Grey-Divider)" }}
              className="px-2 d-flex gap-1 align-items-center h-100"
            >
              <CustomTooltip content={item?.description}>
                <p>{item?.description}</p>
              </CustomTooltip>
            </span>
            <hr
              style={{
                margin: "-1px",
                backgroundColor: "var(--Grey-Divider)",
                opacity: "0.1",
              }}
            />
          </div>
        </td>
        <td className="px-2 normal_text">
          <CustomTooltip content={item?.paymentMethodAccountName}>
            <p>{item?.paymentMethodAccountName}</p>
          </CustomTooltip>
        </td>
        <td className="px-2 normal_text">
          <CustomTooltip content={item?.payeeAccountName}>
            <p>{item?.payeeAccountName}</p>
          </CustomTooltip>
        </td>
        <td className="px-2 normal_text">
          <CustomTooltip content={item?.formatted?.PaymentMethodTypeId}>
            <p style={{ width: "75px" }}>
              {item?.formatted?.PaymentMethodTypeId}
            </p>
          </CustomTooltip>
        </td>
        <td className="px-2 normal_text">
          <CustomTooltip content={item?.reference}>
            <p style={{ width: "75px" }}>{item?.reference}</p>
          </CustomTooltip>
        </td>
        <td className="px-2 normal_text">
          <CustomTooltip content={item?.formatted?.TranactionCreditDebitTypeId}>
            <p style={{ width: "75px" }}>
              {item?.formatted?.TranactionCreditDebitTypeId}
            </p>
          </CustomTooltip>
        </td>
        <td className="px-2 normal_text text-end">
          <CurrencyTooltip
            content={item?.formatted?.AmountRaw}
            formatted={item?.formatted?.Amount}
          >
            <p style={{ width: "75px", textAlign: "start" }}>
              {item?.formatted?.Amount}
            </p>
          </CurrencyTooltip>
        </td>
        <td className="px-2 normal_text text-end">
          <CustomTooltip content={item?.formatted?.PaymentDateDateOnly}>
            <p style={{ width: "75px" }}>{item?.formatted?.PaymentDateDateOnly}</p>
          </CustomTooltip>
        </td>
        <td className="px-2 normal_text">
          <CustomTooltip content={item?.transactionId}>
            <p>{item?.transactionId}</p>
          </CustomTooltip>
        </td>
        {renderActions(item)}
      </tr>
    ),
    [renderActions],
  );

  const renderLoadingOrEmpty = useCallback(
    (isBusy: boolean, rows: TransactionRow[]) => {
      if (isBusy) {
        return (
          <tr>
            <td colSpan={actionColumnSpan} style={{ height: "100px" }}>
              <SimpleSpinner myClassName="Icon40" />
            </td>
          </tr>
        );
      }

      if (rows.length === 0) {
        return (
          <tr>
            <td colSpan={actionColumnSpan}>
              <EmptyState heading="No Data Available" isButton={false} isBorder={false} />
            </td>
          </tr>
        );
      }

      return null;
    },
    [actionColumnSpan],
  );

  const selectedCategoryBadges: {
    values: string[];
    onRemove: (value: string, index: number) => void;
  }[] = useMemo(
    () => [
      {
        values: selectedValues,
        onRemove: (value: string, index: number) => {
          const newValues = selectedValues.filter((_, i) => i !== index);
          const newIds = (selectedIds as number[]).filter(
            (_, i) => i !== index,
          );
          setSelectedValues(newValues);
          setSelectedIds(newIds);
          if (newIds.length === 0) {
            resetFilters();
          } else {
            onApplyFilter(selectedOption, newIds, selectedCategories);
          }
        },
      },
      {
        values: categories,
        onRemove: (value: string) => {
          const newCategories = {
            ...selectedCategories,
            TranactionCreditDebitTypeId:
              selectedCategories.TranactionCreditDebitTypeId.filter(
                (item) => item !== value,
              ),
          };
          setSelectedCategories(newCategories);
          onApplyFilter(selectedOption, selectedIds, newCategories);
        },
      },
      {
        values: payeeAccount,
        onRemove: (value: string) => {
          const newCategories = {
            ...selectedCategories,
            "payeeAccount.accountName": selectedCategories[
              "payeeAccount.accountName"
            ].filter((item) => item !== value),
          };
          setSelectedCategories(newCategories);
          onApplyFilter(selectedOption, selectedIds, newCategories);
        },
      },
      {
        values: paymentMethod,
        onRemove: (value: string) => {
          const newCategories = {
            ...selectedCategories,
            "paymentMethod.PaymentMethodTypeId": selectedCategories[
              "paymentMethod.PaymentMethodTypeId"
            ].filter((item) => item !== value),
          };
          setSelectedCategories(newCategories);
          onApplyFilter(selectedOption, selectedIds, newCategories);
        },
      },
      {
        values: payer,
        onRemove: (value: string) => {
          const newCategories = {
            ...selectedCategories,
            "paymentMethod.accountName": selectedCategories[
              "paymentMethod.accountName"
            ].filter((item) => item !== value),
          };
          setSelectedCategories(newCategories);
          onApplyFilter(selectedOption, selectedIds, newCategories);
        },
      },
    ],
    [
      categories,
      onApplyFilter,
      payeeAccount,
      payer,
      paymentMethod,
      resetFilters,
      selectedCategories,
      selectedIds,
      selectedOption,
      selectedValues,
      setSelectedCategories,
      setSelectedIds,
      setSelectedValues,
    ],
  );

  return (
    <>
      <div className="d-flex flex-wrap gap-2 mt-3 mb-3">
        {selectedCategoryBadges.map(
          (group, groupIndex) =>
            group.values?.map((value: string, index: number) => (
              <p
                key={`${groupIndex}-${value}-${index}`}
                className="selectedValueLabel normal_text"
              >
                {value}
                <AddTraspButton
                  imageValue={GetImages.Close}
                  iconPosition="right"
                  className="closeBtn"
                  onClick={() => group.onRemove(value, index)}
                />
              </p>
            )),
        )}
      </div>

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center flex-grow-1 w-100"
          style={{ minHeight: "60vh" }}
        >
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : (
        <div className="table-responsive fixed-table-header">
          <table className="collapsible-table" style={{ width: "99%" }}>
            <thead className="transaction-table-header">
              <tr>
                <th>
                  <span>Description</span>
                </th>
                <th>
                  <span>From</span>
                </th>
                <th>
                  <span>To</span>
                </th>
                <th>
                  <span>Method</span>
                </th>
                <th>
                  <span>Reference</span>
                </th>
                <th>
                  <span>Category</span>
                </th>
                <th>
                  <span>Amount</span>
                </th>
                <th>
                  <span>Date</span>
                </th>
                <th>
                  <span>Transaction ID</span>
                </th>
                {showActions && (
                  <th style={{ width: "3%" }}>
                    <span>
                      <p>0</p>
                    </span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="h-auto">
              {organizationDataTX.map((org, orgIndex) => (
                <>
                  <tr
                    key={`group-${orgIndex}`}
                    className={`p-12 ${
                      expandedOrganisationIndex === orgIndex ||
                      expandedPropertyIndex === orgIndex
                        ? "expanded"
                        : "collapsed"
                    }`}
                    onClick={() => {
                      setExpandedUnitIndex(null);
                      if (org?.entityId === orgId) {
                        setExpandedOrganisationIndex(
                          expandedOrganisationIndex === orgIndex ? null : orgIndex,
                        );
                        setExpandedPropertyIndex(null);
                        organizationsResponse(org?.entityId);
                        setSelectedPropertyId(null);
                        setSelectedUnitId(null);
                      } else {
                        setExpandedPropertyIndex(
                          expandedPropertyIndex === orgIndex ? null : orgIndex,
                        );
                        setExpandedOrganisationIndex(null);
                        propertyResponseTX(org?.entityId);
                        propertyResponse(org?.entityId);
                        setSelectedPropertyId(org?.entityId);
                        setSelectedUnitId(null);
                      }
                    }}
                  >
                    <td
                      colSpan={5}
                      style={{ width: "48%", borderRight: "0px" }}
                      className="px-2 p-12"
                    >
                      {org?.formatted?.OrganizationGroupTransactionsTypeId ===
                      "Organization" ? (
                        <span className="d-flex align-items-center gap-1">
                          {expandedOrganisationIndex === orgIndex ||
                          expandedPropertyIndex === orgIndex ? (
                            <div className="toggle-btn">-</div>
                          ) : (
                            <div className="toggle-btn">+</div>
                          )}
                          <span className="normal_text fw-600 grouped-title">
                            {org.description}
                          </span>
                        </span>
                      ) : (
                        <span className="d-flex align-items-center gap-1">
                          {expandedOrganisationIndex === orgIndex ||
                          expandedPropertyIndex === orgIndex ? (
                            <div className="toggle-btn">-</div>
                          ) : (
                            <div className="toggle-btn">+</div>
                          )}
                          <img src={GetImages.DarkBlueHome} />
                          <span className="normal_text fw-600 grouped-title">
                            {org.description}
                          </span>
                        </span>
                      )}
                    </td>
                    <td
                      colSpan={5}
                      style={{ width: "50%", borderLeft: "0px" }}
                      className="px-3 p-12"
                    >
                      <div className="d-flex gap-2 align-items-center justify-content-end">
                        <span className="normal_text grey_subtext1 transaction-income">
                          Income:
                        </span>
                        <CurrencyTooltip
                          content={org?.formatted?.IncomeRaw}
                          formatted={org?.formatted?.Income}
                        >
                          <span className="black normal_text fw-600">
                            {org?.formatted?.Income}
                          </span>
                        </CurrencyTooltip>
                        <SeparaterLine
                          height={"26px"}
                          background={"var(--Grey-Divider)"}
                        />

                        <span className="normal_text grey_subtext1 transaction-income">
                          Expenses:
                        </span>
                        <CurrencyTooltip
                          content={org?.formatted?.ExpenseRaw}
                          formatted={org?.formatted?.Expense}
                        >
                          <span className="black normal_text fw-600">
                            {org?.formatted?.Expense}
                          </span>
                        </CurrencyTooltip>
                        <SeparaterLine
                          height={"26px"}
                          background={"var(--Grey-Divider)"}
                        />

                        <span className="normal_text grey_subtext1 transaction-income">
                          Net Income:
                        </span>
                        <CurrencyTooltip
                          content={org?.formatted?.NetIncomeRaw}
                          formatted={org?.formatted?.NetIncome}
                        >
                          <span className="black normal_text fw-600">
                            {org?.formatted?.NetIncome}
                          </span>
                        </CurrencyTooltip>
                      </div>
                    </td>
                  </tr>

                  {expandedOrganisationIndex === orgIndex && (
                    <>
                      {renderLoadingOrEmpty(orgLoading, organizationData) ??
                        organizationData.map((item, txIndex) =>
                          renderTransactionRow(item, `org-${orgIndex}-${txIndex}`),
                        )}
                    </>
                  )}

                  {expandedPropertyIndex === orgIndex && (
                    <>
                      {renderLoadingOrEmpty(propertyLoading, propertyData) ??
                        propertyData.map((item, propertyTxIndex) =>
                          renderTransactionRow(
                            item,
                            `property-${orgIndex}-${propertyTxIndex}`,
                          ),
                        )}
                    </>
                  )}

                  {expandedPropertyIndex === orgIndex &&
                    propertyDataTX.map((item, index) => (
                      <>
                        <tr
                          key={`property-group-${orgIndex}-${index}`}
                          className={`p-12 ${
                            expandedUnitIndex === index ? "expanded" : "collapsed"
                          }`}
                          onClick={() => {
                            setExpandedUnitIndex(
                              expandedUnitIndex === index ? null : index,
                            );
                            if (item?.entityId) {
                              unitResponse(item?.entityId);
                              setSelectedUnitId(item?.entityId);
                            }
                          }}
                        >
                          <td
                            colSpan={5}
                            style={{
                              width: "48%",
                              borderRight: "0px",
                              borderLeft: "none",
                              borderBottom: "none",
                              borderTop: "none",
                            }}
                          >
                            <div className="ps-3 h-100">
                              <span
                                style={{
                                  borderLeft: "1px solid var(--Grey-Divider)",
                                  background: "var(--bg-primary)",
                                }}
                                className="p-2 d-flex gap-1 h-100 align-items-center"
                              >
                                {expandedUnitIndex === index ? (
                                  <div className="toggle-btn">-</div>
                                ) : (
                                  <div className="toggle-btn">+</div>
                                )}
                                <img
                                  src={GetImages.UnitIconBlue}
                                  className="iconstyle16 color-white-icon"
                                />
                                <span className="normal_text fw-500 d-flex align-items-center">
                                  {item.description}
                                </span>
                              </span>
                              <hr
                                style={{
                                  margin: "0px",
                                  backgroundColor: "var(--Grey-Divider)",
                                  opacity: "0.1",
                                }}
                              />
                            </div>
                          </td>
                          <td
                            colSpan={5}
                            style={{
                              width: "50%",
                              borderLeft: "0px",
                              background: "var(--bg-primary)",
                            }}
                            className="px-3"
                          >
                            <div className="d-flex gap-2 align-items-center justify-content-end">
                              <span className="normal_text grey_subtext1 transaction-income">
                                Income:
                              </span>
                              <CurrencyTooltip
                                content={item?.formatted?.IncomeRaw}
                                formatted={item?.formatted?.Income}
                              >
                                <span className="black normal_text fw-600">
                                  {item?.formatted?.Income}
                                </span>
                              </CurrencyTooltip>
                              <SeparaterLine
                                height={"26px"}
                                background={"var(--Grey-Divider)"}
                              />
                              <span className="normal_text grey_subtext1 transaction-income">
                                Expenses:
                              </span>
                              <CurrencyTooltip
                                content={item?.formatted?.ExpenseRaw}
                                formatted={item?.formatted?.Expense}
                              >
                                <span className="black normal_text fw-600">
                                  {item?.formatted?.Expense}
                                </span>
                              </CurrencyTooltip>
                              <SeparaterLine
                                height={"26px"}
                                background={"var(--Grey-Divider)"}
                              />
                              <span className="normal_text grey_subtext1 transaction-income">
                                Net Income:
                              </span>
                              <CurrencyTooltip
                                content={item?.formatted?.NetIncomeRaw}
                                formatted={item?.formatted?.NetIncome}
                              >
                                <span className="black normal_text fw-600">
                                  {item?.formatted?.NetIncome}
                                </span>
                              </CurrencyTooltip>
                            </div>
                          </td>
                        </tr>

                        {expandedUnitIndex === index && (
                          <>
                            {renderLoadingOrEmpty(unitLoading, unitData) ??
                              unitData.map((unitItem, unitTxIndex) =>
                                renderTransactionRow(
                                  unitItem,
                                  `unit-${orgIndex}-${index}-${unitTxIndex}`,
                                ),
                              )}
                          </>
                        )}
                      </>
                    ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EditTransactionDrawer
        isOpen={!!transactionToEdit}
        transaction={transactionToEdit}
        paymentMethods={paymentMethodsArr}
        payeeAccounts={payeeAccountData}
        onClose={() => setTransactionToEdit(null)}
        onSaved={refreshVisibleData}
      />

      <ConfirmationPopup
        isOpen={!!transactionToDelete}
        title="Delete Transaction"
        message="Do you want to delete this transaction?"
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDelete}
        onSecondaryAction={() => setTransactionToDelete(null)}
        onClose={() => setTransactionToDelete(null)}
        isPrimaryDestructive={true}
        isLoading={isDeleting}
        loadingLabel="Deleting..."
      />

      <ConfirmationPopup
        isOpen={!!transactionToRefund}
        title="Refund Transaction"
        message="Do you want to refund this transaction?"
        primaryButtonLabel="Refund"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleRefund}
        onSecondaryAction={() => setTransactionToRefund(null)}
        onClose={() => setTransactionToRefund(null)}
        isLoading={isRefunding}
        loadingLabel="Refunding..."
      />
    </>
  );
};

export default TransactionTable;
